import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { createApi } from '../api';

interface AdminStats {
  timestamp: string;
  users: {
    total: number;
  };
  streams: {
    total: number;
  };
  tasks: {
    total: number;
    completed: number;
    active: number;
    byPhase: Record<string, number>;
  };
}

const AdminPage: React.FC = () => {
  const { user, getIdToken } = useAuth();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Hardcoded admin user ID - only you can access this page
  const ADMIN_USER_ID = 'Y0LK1yKFX2UyFibDYedA4vm0rwF3';

  useEffect(() => {
    if (user && user.uid === ADMIN_USER_ID) {
      loadStats();
    } else if (user) {
      setError('Access denied');
      setLoading(false);
    }
  }, [user]);

  const loadStats = async () => {
    try {
      const token = await getIdToken();
      const api = createApi(token);
      const response = await fetch('/api/admin/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch admin stats');
      }

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to load admin stats:', error);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.uid !== ADMIN_USER_ID) {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <h1>Access Denied</h1>
          <p>You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <h1>Admin Dashboard</h1>
          <p>Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <h1>Admin Dashboard</h1>
          <p className="error">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p className="admin-subtitle">TaskMaster Application Statistics</p>
        <p className="admin-timestamp">Last updated: {new Date(stats.timestamp).toLocaleString()}</p>
      </div>

      <div className="admin-stats-grid">
        <div className="stat-card">
          <h3>Users</h3>
          <div className="stat-number">{stats.users.total}</div>
          <div className="stat-label">Total Users</div>
        </div>

        <div className="stat-card">
          <h3>Streams</h3>
          <div className="stat-number">{stats.streams.total}</div>
          <div className="stat-label">Total Streams</div>
        </div>

        <div className="stat-card">
          <h3>Tasks</h3>
          <div className="stat-number">{stats.tasks.total}</div>
          <div className="stat-label">Total Tasks</div>
        </div>

        <div className="stat-card">
          <h3>Active Tasks</h3>
          <div className="stat-number">{stats.tasks.active}</div>
          <div className="stat-label">In Progress</div>
        </div>

        <div className="stat-card">
          <h3>Completed Tasks</h3>
          <div className="stat-number">{stats.tasks.completed}</div>
          <div className="stat-label">Done</div>
        </div>
      </div>

      <div className="admin-section">
        <h2>Tasks by Phase</h2>
        <div className="phase-stats">
          {Object.entries(stats.tasks.byPhase).map(([phase, count]) => (
            <div key={phase} className="phase-stat">
              <span className="phase-name">{phase}</span>
              <span className="phase-count">{count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-actions">
        <button onClick={loadStats} className="refresh-btn">
          Refresh Statistics
        </button>
      </div>
    </div>
  );
};

export default AdminPage;